import React, { useState, useEffect, useRef } from "react";

import axios from "axios";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";

import SeasonalDiscountDialog from "../components/SeasonalDiscountDialog";

const SeasonalDiscounts = () => {
  const toast = useRef(null);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [height, setHeight] = useState(0);

  const dt = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.innerHeight;
      const specificAmount = 320;
      setHeight(vh - specificAmount);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get("/api/seasonalDiscounts");
      if (response.data.constructor === Array) setData(response.data);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while getting data. Please try again.",
        life: 3000,
      });
    }
  };

  const editDiscount = (rowData) => {
    setSelected(rowData);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
  };

  const handleDelete = (value) => {
    confirmDialog({
      message: "Are you sure you want to delete this seasonal discount?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => confirmDelete(value),
    });
  };

  const confirmDelete = async (value) => {
    axios
      .delete(`/api/seasonalDiscounts/${value.id}`)
      .then(() => {
        toast.current.show({
          severity: "success",
          summary: "Discount Deleted",
          detail: "Seasonal discount has been successfully deleted.",
          life: 3000,
        });
        fetchData();
      })
      .catch(() => {
        toast.current.show({
          severity: "error",
          summary: "Discount Error",
          detail:
            "An error occurred while deleting the seasonal discount. Please try again.",
          life: 3000,
        });
      });
  };

  const handleActivate = async (rowData) => {
    try {
      await axios.post(`/api/seasonalDiscounts/activate/${rowData.id}`);
      toast.current.show({
        severity: "success",
        summary: "Discount Activated",
        detail: "Seasonal discount has been successfully activated.",
        life: 3000,
      });
      fetchData();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Activation Error",
        detail:
          "An error occurred while activating the seasonal discount. Please try again.",
        life: 3000,
      });
    }
  };

  const handleDeactivate = async (rowData) => {
    try {
      await axios.post(`/api/seasonalDiscounts/deactivate/${rowData.id}`);
      toast.current.show({
        severity: "success",
        summary: "Discount Deactivated",
        detail: "Seasonal discount has been successfully deactivated.",
        life: 3000,
      });
      fetchData();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Deactivation Error",
        detail:
          "An error occurred while deactivating the seasonal discount. Please try again.",
        life: 3000,
      });
    }
  };

  const typeTemplate = (rowData) => {
    return rowData.isPercentage ? "Percentage" : "Fixed Amount (LKR)";
  };

  const valueTemplate = (rowData) => {
    return rowData.isPercentage
      ? rowData.value + "%"
      : "LKR " + rowData.value;
  };

  const statusTemplate = (rowData) => {
    return rowData.isActive ? (
      <Tag value="Active" severity="success" />
    ) : (
      <Tag value="Inactive" severity="danger" />
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <>
        <Button
          className="p-button-rounded p-button-info mr-2"
          icon="pi pi-pencil"
          tooltip="Edit"
          tooltipOptions={{ event: "both" }}
          onClick={() => {
            editDiscount(rowData);
          }}
        />
        {rowData.isActive ? (
          <Button
            className="p-button-rounded p-button-warning mr-2"
            icon="pi pi-ban"
            tooltip="Deactivate"
            tooltipOptions={{ event: "both" }}
            onClick={() => handleDeactivate(rowData)}
          />
        ) : (
          <Button
            className="p-button-rounded p-button-success mr-2"
            icon="pi pi-check"
            tooltip="Activate"
            tooltipOptions={{ event: "both" }}
            onClick={() => handleActivate(rowData)}
          />
        )}
        <Button
          className="p-button-rounded p-button-danger"
          icon="pi pi-trash"
          tooltip="Delete"
          tooltipOptions={{ event: "both" }}
          onClick={() => handleDelete(rowData)}
        />
      </>
    );
  };

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  return (
    <div className="card flex flex-column gap-4">
      <Toast ref={toast} position="bottom-right" />
      <div className="flex justify-content-between align-items-center">
        <div>
          <h4 className="text-primary mb-1">Seasonal Discounts</h4>
          <label className="text-secondary">
            Configure seasonal discounts for the website
          </label>
        </div>
        <div className="flex gap-2 align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="p-inputtext-sm"
              type="search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
            />
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            severity="help"
            onClick={fetchData}
          />
          <Button
            label="Export"
            icon="pi pi-file-excel"
            severity="success"
            onClick={() => exportCSV(false)}
          />
          <Button
            label="Add Discount"
            icon="pi pi-plus"
            className="p-button-primary p-mb-3"
            onClick={() => editDiscount(null)}
          />
        </div>
      </div>
      <Dialog
        visible={dialogVisible}
        onHide={hideDialog}
        header="Add / Edit Seasonal Discount"
        style={{ width: "400px", minHeight: "fit-content" }}
      >
        <SeasonalDiscountDialog
          discount={selected}
          onCancel={hideDialog}
          onSubmit={() => {
            hideDialog();
            fetchData();
          }}
        />
      </Dialog>
      <DataTable
        ref={dt}
        value={data}
        className="p-datatable-striped"
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        globalFilter={globalFilter}
        scrollable
        scrollHeight={height}
      >
        <Column
          header=""
          body={actionTemplate}
          style={{ width: "150px" }}
        ></Column>
        <Column sortable field="name" header="Name" />
        <Column sortable header="Type" body={typeTemplate} />
        <Column sortable header="Value" body={valueTemplate} />
        <Column sortable header="Status" body={statusTemplate} />
      </DataTable>
    </div>
  );
};

export default SeasonalDiscounts;
